import { useState } from 'react'
import { Icon } from '@iconify/react'
import { AUDIT_TRAIL_ENTRIES } from '../../utils/complianceMockData'
import LoadingSpinner from '../common/LoadingSpinner'
import Select from '../common/Select'

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  })
}

const ScoreBadge = ({ score }) => {
  const color =
    score >= 90
      ? 'text-green-700 bg-green-50 border-green-200'
      : score >= 70
        ? 'text-yellow-700 bg-yellow-50 border-yellow-200'
        : 'text-red-700 bg-red-50 border-red-200'

  return (
    <span className={`text-sm font-bold px-2.5 py-1 rounded-lg border ${color}`}>
      {score}%
    </span>
  )
}

const ComparisonMetric = ({ label, before, after, improved }) => (
  <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
    <p className="text-xs text-gray-500 mb-2">{label}</p>
    <div className="flex items-center justify-center gap-2">
      <span className="text-lg font-semibold text-gray-400 line-through">{before}</span>
      <Icon icon="mdi:arrow-right" className="text-gray-400 text-sm" />
      <span
        className={`text-lg font-bold ${improved ? 'text-green-600' : before === after ? 'text-gray-700' : 'text-red-600'}`}
      >
        {after}
      </span>
    </div>
  </div>
)

const InlineState = ({ icon, title, message, actionLabel, onAction }) => (
  <div className="text-center py-12 px-4 rounded-xl border border-dashed border-border-light bg-gray-50">
    <Icon icon={icon} className="text-4xl mx-auto mb-3 text-gray-400" />
    <p className="text-sm font-semibold text-text-dark">{title}</p>
    <p className="text-sm text-text-secondary mt-1">{message}</p>
    {actionLabel && (
      <button
        type="button"
        onClick={onAction}
        className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-hover transition-colors"
      >
        <Icon icon="mdi:refresh" />
        {actionLabel}
      </button>
    )}
  </div>
)

const AuditEntry = ({ entry, isExpanded, onToggle }) => {
  const scoreDelta = entry.after.complianceScore - entry.before.complianceScore
  const violationsDelta = entry.before.totalViolations - entry.after.totalViolations

  return (
    <div className="relative pl-8 pb-8 last:pb-0">
      <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-primary border-4 border-white shadow-md z-10" />
      <div className="absolute left-[7px] top-5 bottom-0 w-0.5 bg-border-light last:hidden" />

      <div className="bg-white rounded-xl border border-border-light shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={onToggle}
          className="w-full text-left p-4 md:p-5 hover:bg-gray-50/50 transition-colors"
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 text-xs font-mono font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                  <Icon icon="mdi:identifier" className="text-sm" />
                  {entry.scanId}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  <Icon icon="mdi:shield-check" className="text-sm" />
                  Verified Timestamp
                </span>
              </div>

              <p className="text-sm font-semibold text-text-dark mb-1">{entry.websiteName}</p>
              <p className="text-xs text-text-secondary break-all mb-2">{entry.url}</p>

              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <Icon icon="mdi:clock-outline" />
                  {formatDateTime(entry.scannedAt)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Icon icon="mdi:file-document-outline" />
                  {entry.scanType}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Icon icon="mdi:scale-balance" />
                  {entry.wcagLevel}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Before</p>
                <ScoreBadge score={entry.before.complianceScore} />
              </div>
              <Icon icon="mdi:arrow-right-bold" className="text-primary text-xl" />
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">After</p>
                <ScoreBadge score={entry.after.complianceScore} />
              </div>
              {scoreDelta > 0 && (
                <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  +{scoreDelta}%
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              {entry.autoFixesApplied} auto-fixes applied · {violationsDelta} violations resolved
            </span>
            <Icon
              icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              className="text-gray-400 text-xl"
            />
          </div>
        </button>

        {isExpanded && (
          <div className="px-4 md:px-5 pb-5 border-t border-gray-100 bg-gray-50/50 animate-fade-in">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mt-4 mb-3">
              Before / After Comparison
            </p>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <ComparisonMetric
                label="Compliance Score"
                before={`${entry.before.complianceScore}%`}
                after={`${entry.after.complianceScore}%`}
                improved={scoreDelta > 0}
              />
              <ComparisonMetric
                label="Total Violations"
                before={entry.before.totalViolations}
                after={entry.after.totalViolations}
                improved={violationsDelta > 0}
              />
              <ComparisonMetric
                label="Critical"
                before={entry.before.critical}
                after={entry.after.critical}
                improved={entry.before.critical > entry.after.critical}
              />
              <ComparisonMetric
                label="Serious"
                before={entry.before.serious}
                after={entry.after.serious}
                improved={entry.before.serious > entry.after.serious}
              />
              <ComparisonMetric
                label="Moderate"
                before={entry.before.moderate}
                after={entry.after.moderate}
                improved={entry.before.moderate > entry.after.moderate}
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-white border border-border-light">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Icon icon="mdi:fingerprint" className="text-primary text-base" />
                <span>
                  Record hash: <code className="font-mono text-gray-700">{entry.hash}</code>
                </span>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded-full">
                <Icon icon="mdi:gavel" className="text-sm" />
                Court-admissible evidence
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const AuditTrailSection = ({ isLoading = false, error = null, onRetry }) => {
  const [expandedId, setExpandedId] = useState(AUDIT_TRAIL_ENTRIES[0]?.id)
  const [websiteFilter, setWebsiteFilter] = useState('all')
  const [visibleCount, setVisibleCount] = useState(3)

  const filteredEntries =
    websiteFilter === 'all'
      ? AUDIT_TRAIL_ENTRIES
      : AUDIT_TRAIL_ENTRIES.filter((e) => e.websiteId === websiteFilter)

  const visibleEntries = filteredEntries.slice(0, visibleCount)
  const hasMoreEntries = visibleCount < filteredEntries.length
  const websites = [...new Map(AUDIT_TRAIL_ENTRIES.map((e) => [e.websiteId, e.websiteName])).entries()]

  const handleWebsiteChange = (event) => {
    setWebsiteFilter(event.target.value)
    setVisibleCount(3)
    setExpandedId(null)
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-text-dark">Timestamped Audit Trail</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            Every scan result is permanently logged with date/time and before/after comparison.
          </p>
        </div>
        <Select
          value={websiteFilter}
          onChange={handleWebsiteChange}
          disabled={isLoading}
          className="w-full sm:w-48"
          wrapperClassName=""
        >
          <option value="all">All websites</option>
          {websites.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
      </div>

      <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-indigo-50 to-primary/5 border border-indigo-100 flex items-start gap-3">
        <Icon icon="mdi:information-outline" className="text-indigo-600 text-xl shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-indigo-900">Immutable audit records</p>
          <p className="text-xs text-indigo-700/80 mt-0.5">
            Each entry is timestamped and cryptographically hashed. This trail serves as court-admissible
            evidence of your ongoing good-faith accessibility compliance efforts.
          </p>
        </div>
      </div>

      {error && (
        <InlineState
          icon="mdi:alert-circle-outline"
          title="Audit trail could not be loaded"
          message="We could not fetch the scan records right now. Please try again."
          actionLabel="Retry"
          onAction={onRetry}
        />
      )}

      {isLoading && (
        <div className="py-12 rounded-xl border border-border-light bg-white">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!isLoading && !error && filteredEntries.length === 0 && (
        <InlineState
          icon="mdi:timeline-clock-outline"
          title="No scans recorded yet"
          message="Once your first website scan completes, timestamped audit records will appear here."
        />
      )}

      <div className="relative">
        {!isLoading && !error && visibleEntries.map((entry) => (
          <AuditEntry
            key={entry.id}
            entry={entry}
            isExpanded={expandedId === entry.id}
            onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
          />
        ))}
      </div>

      {!isLoading && !error && hasMoreEntries && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount((count) => count + 3)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border-light text-sm font-semibold text-text-dark hover:border-primary hover:text-primary transition-colors"
          >
            <Icon icon="mdi:chevron-down" />
            Load more audit records
          </button>
        </div>
      )}
    </div>
  )
}

export default AuditTrailSection
